import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Grid, MenuItem, FormControl,
    InputLabel, Select, Autocomplete
} from '@mui/material';
import axios from 'axios';
import { API_URL } from '../../config/api'; // Assuming this exists or similar

interface Product {
    id?: string;
    name: string;
    sku: string;
    description: string;
    price: number;
    costPrice: number;
    stockQuantity: number;
    minQuantity: number;
    type: 'GOODS' | 'SERVICE';
    category: string;
}

interface AddProductModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    productToEdit?: Product | null;
}

const DEFAULT_CATEGORIES = [
    'Uniforms',
    'Stationery',
    'Textbooks',
    'Sports Gear',
    'Electronics',
    'Food & Beverages',
    'General Supplies'
];

const AddProductModal: React.FC<AddProductModalProps> = ({ open, onClose, onSuccess, productToEdit }) => {
    const [formData, setFormData] = useState<Product>({
        name: '',
        sku: '',
        description: '',
        price: 0,
        costPrice: 0,
        stockQuantity: 0,
        minQuantity: 5,
        type: 'GOODS',
        category: '',
    });

    const [preloadedCategories, setPreloadedCategories] = useState<string[]>(DEFAULT_CATEGORIES);

    const getHeaders = useCallback(() => {
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        return { headers: { Authorization: `Bearer ${token}` } };
    }, []);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/inventory/categories`, getHeaders());
            const uniqueCats = Array.from(new Set([...DEFAULT_CATEGORIES, ...response.data]));
            setPreloadedCategories(uniqueCats);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }, [getHeaders]);

    useEffect(() => {
        if (open) {
            fetchCategories();
        }
        if (productToEdit) {
            setFormData(productToEdit);
        } else {
            setFormData({
                name: '',
                sku: '',
                description: '',
                price: 0,
                costPrice: 0,
                stockQuantity: 0,
                minQuantity: 5,
                type: 'GOODS',
                category: '',
            });
        }
    }, [productToEdit, open, fetchCategories]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name as string]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            const headers = getHeaders();
            if (productToEdit?.id) {
                await axios.put(`${API_URL}/inventory/${productToEdit.id}`, formData, headers);
            } else {
                await axios.post(`${API_URL}/inventory`, formData, headers);
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving product:', error);
            // Handle error (show alert/toast)
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{productToEdit ? 'Edit Product' : 'Add New Product'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Product Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="SKU"
                            name="sku"
                            value={formData.sku}
                            onChange={handleChange}
                            required
                            disabled={!!productToEdit}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            multiline
                            rows={2}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select
                                name="type"
                                value={formData.type}
                                label="Type"
                                onChange={(e: any) => handleChange(e)}
                            >
                                <MenuItem value="GOODS">Goods (Track Stock)</MenuItem>
                                <MenuItem value="SERVICE">Service (No Stock)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Autocomplete
                            freeSolo
                            options={preloadedCategories}
                            value={formData.category}
                            onInputChange={(event, newValue) => {
                                setFormData(prev => ({ ...prev, category: newValue }));
                            }}
                            onChange={(event, newValue) => {
                                setFormData(prev => ({ ...prev, category: newValue as string || '' }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category (e.g. Uniforms)"
                                    name="category"
                                    required
                                    placeholder="Type or select a category"
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Selling Price"
                            name="price"
                            type="number"
                            value={formData.price}
                            onChange={handleChange}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Cost Price"
                            name="costPrice"
                            type="number"
                            value={formData.costPrice}
                            onChange={handleChange}
                        />
                    </Grid>
                    {formData.type === 'GOODS' && (
                        <>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Current Stock"
                                    name="stockQuantity"
                                    type="number"
                                    value={formData.stockQuantity}
                                    onChange={handleChange}
                                    disabled={!!productToEdit} // Stock adjustment via specific endpoint usually
                                    helperText={productToEdit ? "Use Stock Adjustment to change quantity" : "Initial Stock"}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Min Quantity Alert"
                                    name="minQuantity"
                                    type="number"
                                    value={formData.minQuantity}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddProductModal;
